import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { forexApi, type FollowRelation } from "@/lib/forex-api";

const modeLabels: Record<string, string> = {
  fixed_lot: "固定手数",
  proportional: "比例跟单",
  fixed_ratio: "倍数跟单",
};

const statusChip: Record<string, { cls: string; label: string }> = {
  active: { cls: "bg-emerald-500/10 text-emerald-500", label: "活跃" },
  paused: { cls: "bg-amber-500/10 text-amber-500", label: "暂停" },
  stopped: { cls: "bg-red-500/10 text-red-500", label: "已停止" },
};

type ActionState = "processing" | "success";

function formatDate(timestamp: number) {
  return new Date(timestamp * 1000).toISOString().slice(0, 10);
}

export default function FollowMonitorTab() {
  const [relations, setRelations] = useState<FollowRelation[]>([]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("active");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [actionStates, setActionStates] = useState<Record<string, { action: string; state: ActionState }>>({});
  const [toast, setToast] = useState<{ type: "success" | "error"; msg: string } | null>(null);
  const [updatedAt, setUpdatedAt] = useState<Date | null>(null);
  const [revision, setRevision] = useState(0);

  useEffect(() => {
    forexApi
      .listFollowRelations()
      .then((data) => { setRelations(data.items); setUpdatedAt(new Date()); setError(""); })
      .catch(() => setError("跟单关系加载失败"))
      .finally(() => setLoading(false));
  }, [revision]);

  const filtered = useMemo(
    () =>
      relations.filter((r) => {
        if (statusFilter && r.status !== statusFilter) return false;
        if (
          search &&
          !r.follower_name.includes(search) &&
          !r.agent_name.includes(search) &&
          !r.relation_id.includes(search) &&
          !r.symbols.join(",").includes(search.toUpperCase())
        ) {
          return false;
        }
        return true;
      }),
    [relations, search, statusFilter],
  );

  const showToast = (type: "success" | "error", msg: string) => {
    setToast({ type, msg });
    setTimeout(() => setToast(null), 2500);
  };

  const handleAction = async (relationId: string, action: "pause" | "resume" | "stop", label: string) => {
    setActionStates((prev) => ({ ...prev, [relationId]: { action, state: "processing" } }));
    try {
      const updated = await forexApi.patchFollowRelation(relationId, { action, reason: label });
      setRelations((prev) => prev.map((item) => (item.relation_id === updated.relation_id ? updated : item)));
      setActionStates((prev) => ({ ...prev, [relationId]: { action, state: "success" } }));
      showToast("success", label);
    } catch {
      showToast("error", "操作失败，请稍后重试");
    } finally {
      setActionStates((prev) => {
        const next = { ...prev };
        delete next[relationId];
        return next;
      });
    }
  };

  return (
    <div className="space-y-4">
      <div><h2 className="text-lg font-semibold">跟单关系</h2><p className="text-xs text-text-muted mt-1">关系状态只表示跟随配置状态，交易资格和实际成交请在执行详情核对。</p></div>
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-text-muted text-sm" />
          <input
            type="text"
            placeholder="搜索跟单者/智能体/品种..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-9 w-[240px] pl-9 pr-3 rounded-lg bg-surface text-sm text-text-primary placeholder:text-text-muted border border-surface-border focus:border-brand/50 focus:outline-none transition-colors"
          />
        </div>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="h-9 px-3 rounded-lg bg-surface text-sm text-text-primary border border-surface-border focus:border-brand/50 focus:outline-none cursor-pointer">
          <option value="">全部状态</option>
          <option value="active">活跃</option>
          <option value="paused">暂停</option>
          <option value="stopped">已停止</option>
        </select>
        <button type="button" onClick={() => { setLoading(true); setRevision(value => value + 1); }} className="text-xs text-brand">刷新</button>
        <span className="text-xs text-text-muted ml-auto">{loading ? "加载中..." : `当前显示 ${filtered.length} / 已加载 ${relations.length} 条`}{updatedAt ? ` · 更新于 ${updatedAt.toLocaleString("zh-CN")}` : ""}</span>
      </div>

      {error && <div className="rounded-lg border border-red-500/20 bg-red-500/5 px-4 py-3 text-sm text-red-500">{error}</div>}
      {!loading && !error && !filtered.length && <p className="rounded-lg border bg-white p-5 text-sm text-text-muted">{relations.length ? "当前筛选下没有跟单关系" : "暂无跟单关系"}</p>}

      <div className="bg-white rounded-xl border border-surface-border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[1150px]">
            <thead>
              <tr className="border-b border-surface-border bg-surface/50">
                <th className="text-left px-4 py-3 text-text-secondary font-medium text-xs whitespace-nowrap">关系ID</th>
                <th className="text-left px-4 py-3 text-text-secondary font-medium text-xs whitespace-nowrap">跟单账号</th>
                <th className="text-left px-4 py-3 text-text-secondary font-medium text-xs whitespace-nowrap">跟随智能体</th>
                <th className="text-left px-4 py-3 text-text-secondary font-medium text-xs whitespace-nowrap">支持品种</th>
                <th className="text-center px-4 py-3 text-text-secondary font-medium text-xs whitespace-nowrap">跟单模式</th>
                <th className="text-right px-4 py-3 text-text-secondary font-medium text-xs whitespace-nowrap">手数/比例</th>
                <th className="text-right px-4 py-3 text-text-secondary font-medium text-xs whitespace-nowrap">最大回撤</th>
                <th className="text-center px-4 py-3 text-text-secondary font-medium text-xs whitespace-nowrap">状态</th>
                <th className="text-right px-4 py-3 text-text-secondary font-medium text-xs whitespace-nowrap">累计盈亏</th>
                <th className="text-left px-4 py-3 text-text-secondary font-medium text-xs whitespace-nowrap">启用时间</th>
                <th className="text-center px-4 py-3 text-text-secondary font-medium text-xs whitespace-nowrap sticky right-0 bg-surface">操作</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => {
                const act = actionStates[r.relation_id];
                const chip = statusChip[r.status] || statusChip.paused;
                return (
                  <tr key={r.relation_id} className="border-b border-surface-border hover:bg-surface/30 transition-colors">
                    <td className="px-4 py-3 text-text-primary font-mono text-xs">{r.relation_id}</td>
                    <td className="px-4 py-3">
                      <p className="text-text-primary text-sm whitespace-nowrap">{r.follower_name}</p>
                      <p className="text-text-muted text-[11px] font-mono">{r.follower_account_masked}</p>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-text-primary text-sm whitespace-nowrap">{r.agent_name}</span>
                      <span className="text-text-muted text-[11px] ml-1 font-mono">{r.agent_id}</span>
                    </td>
                    <td className="px-4 py-3 text-text-primary font-mono text-xs whitespace-nowrap">{r.symbols.join(", ")}</td>
                    <td className="px-4 py-3 text-center">
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] bg-brand/10 text-brand whitespace-nowrap">{modeLabels[r.copy_mode] || r.copy_mode}</span>
                    </td>
                    <td className="px-4 py-3 text-right text-text-primary font-mono text-xs">{r.copy_mode === "fixed_lot" ? `${r.copy_value} 手` : `${(r.copy_value * 100).toFixed(0)}%`}</td>
                    <td className="px-4 py-3 text-right"><span className="font-mono text-xs text-amber-500">{r.max_drawdown}%</span></td>
                    <td className="px-4 py-3 text-center"><span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11px] font-medium whitespace-nowrap ${chip.cls}`}>{chip.label}</span></td>
                    <td className="px-4 py-3 text-right"><span className={`font-mono text-sm whitespace-nowrap ${r.total_pnl >= 0 ? "text-emerald-500" : "text-red-500"}`}>{r.total_pnl >= 0 ? "+" : ""}{r.total_pnl.toLocaleString("en-US", { minimumFractionDigits: 2 })}</span><div className="text-[11px] text-text-muted">币种未提供</div></td>
                    <td className="px-4 py-3 text-text-muted text-xs whitespace-nowrap">{formatDate(r.opened_at)}</td>
                    <td className="px-4 py-3 text-center sticky right-0 bg-white">
                      <div className="flex items-center justify-center gap-1">
                        <Link to={`/forex/follow/detail?id=${r.relation_id}`} className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium bg-surface text-text-secondary hover:bg-brand/10 hover:text-brand transition-colors whitespace-nowrap cursor-pointer"><i className="ri-eye-line text-xs" />详情</Link>
                        {r.status === "active" && <button onClick={() => handleAction(r.relation_id, "pause", "已暂停跟单")} disabled={!!act} className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs bg-amber-500/10 text-amber-500 hover:bg-amber-500/20 disabled:opacity-60 whitespace-nowrap">{act?.action === "pause" ? "处理中" : "暂停"}</button>}
                        {r.status === "paused" && <button onClick={() => handleAction(r.relation_id, "resume", "已恢复跟单")} disabled={!!act} className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs bg-emerald-500/10 text-emerald-500 hover:bg-emerald-500/20 disabled:opacity-60 whitespace-nowrap">{act?.action === "resume" ? "处理中" : "恢复"}</button>}
                        {r.status !== "stopped" && <button onClick={() => handleAction(r.relation_id, "stop", "已停止跟单")} disabled={!!act} className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs bg-red-500/10 text-red-500 hover:bg-red-500/20 disabled:opacity-60 whitespace-nowrap">停止</button>}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {toast && (
        <div className="fixed bottom-6 right-6 z-50">
          <div className={`flex items-center gap-2 px-4 py-3 rounded-lg shadow-lg text-sm font-medium ${toast.type === "success" ? "bg-emerald-500 text-white" : "bg-red-500 text-white"}`}>
            <i className={`text-base ${toast.type === "success" ? "ri-checkbox-circle-fill" : "ri-error-warning-fill"}`} />{toast.msg}
          </div>
        </div>
      )}
    </div>
  );
}
